import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { addEmailTemplateId,
  addEmailTemplateVariables,
  addEmailTemplateVersionId,
  addEmailSender,
  clearBatch,
  addHTML,
} from "state/reducers/communicationBatching";
import { useLazyQuery } from "@apollo/react-hooks";
import { QUERY_TEMPLATE, QUERY_ALL_TEMPLATES } from "utils/mutations";
import { Loading, Dropdown, SearchBar } from "components/common";
import { useCommsBatching } from "utils";
import MarketingButtons from "./utils/components/MarketingButtons";
import MarketingNavigation from "./utils/components/MarketingNavigation";
import MarketingStepper from "./utils/components/MarketingStepper";
import MarketingContainer from "./utils/components/MarketingContainer";

const SENDERS = [
  "noreply@tomocredit.com",
  "help@tomocredit.com",
  "boost@tomocredit.com",
  "invitation@tomomarketplace.com",
  "invitation@tomo.deals",
  "collections@tomocredit.com",
];

const dedupJoin = (arrs) => {
  const output = [];
  arrs.forEach((arr) => {
    arr.forEach((val) => {
      if (!output.includes(val)) {
        output.push(val);
      }
    });
  });

  return output;
};

const BatchEmail = () => {
  const [pages] = useState(25);
  const [currentPage] = useState(0);
  const [templateSearchInput, setTemplateSearchInput] = useState("");
  const [templates, setTemplates] = useState([]);
  const [html, setHtml] = useState(null);
  const { getPreviousLink, getNextLink } = useCommsBatching({ currentPage: 'email' });
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    emailTemplateVariables,
    emailTemplateId,
    emailTemplateVersionId,
    emailSender,
    batchId,
  } = useSelector((state) => state.communicationBatching);

  if (!batchId) {
    dispatch(clearBatch());
    history.push('/v2-marketing');
  }

  const [
    fetchTemplate,
    { data: templateData },
  ] = useLazyQuery(QUERY_TEMPLATE, { fetchPolicy: 'network-only' });

  const [
    getAllTemplates,
    { data: templatesData, loading: allTemplatesLoading },
  ] = useLazyQuery(QUERY_ALL_TEMPLATES, {
    onCompleted: async () => {
      const allTemplates = (templatesData || {}).templates || [];
      if (emailTemplateId) {
        setTemplates(allTemplates.filter((template) => template.id === emailTemplateId));
        await fetchTemplate({
          variables: {
            templateId: emailTemplateId,
            versionId: emailTemplateVersionId,
          },
        });
      } else {
        setTemplates(allTemplates);
      }
    },
  });

  useEffect(() => {

    // only changes once on page load.
    if (!templatesData) {
      dispatch(getAllTemplates);
    }

    // whenever we select a different template...
    if (templateData) {
      const { html_content, subject } = templateData.template || {};
      // if (!html_content) { setError("error loading template"); }
      const cleansedHtml = DOMPurify.sanitize(html_content);
      setHtml(cleansedHtml);
      const variables = cleansedHtml
        .split("{{")
        .map((item) => {
          const secondSplit = item.split("}}");
          if (secondSplit.length === 1) { return null; }
          return secondSplit[0];
        })
        .filter((s) => s && !s.includes("unsubscribe"));

      const subjectVariables = subject
        .split("{{")
        .map((item) => {
          const secondSplit = item.split("}}");
          if (secondSplit.length === 1) { return null; }
          return secondSplit[0];
        })
        .filter((s) => s && !s.includes("unsubscribe"));

      dispatch(addEmailTemplateVariables(dedupJoin([variables, subjectVariables])));
    }

  }, [templateData, templates]);

  if (allTemplatesLoading) {
    return (<Loading/>);
  }

  const handleDiscardChanges = () => {
    dispatch(clearBatch());
    history.push("/marketing");
  };

  const handleNext = () => {
    dispatch(addHTML(html));
  };

  const searchTemplate = (value = '') => {
    const allTemplates = (templatesData || {}).templates || [];
    setTemplateSearchInput(value);
    if (value) {
      const matches = allTemplates.filter((template) => {
        return template.id === value || template.name?.startsWith(value);
      });
      setTemplates(matches);
    } else {
      setTemplates(allTemplates);
    }

    setHtml(null);
    dispatch(addEmailTemplateId(""));
    dispatch(addEmailTemplateVersionId(""));
  };

  return (
    <MarketingContainer>
      <MarketingNavigation/>
      <MarketingStepper />
      <MarketingButtons
        discardButton="Discard"
        discardChanges={handleDiscardChanges}
        prevButton="Previous"
        prevLink={getPreviousLink()}
        nextButton="Continue"
        nextLink={getNextLink()}
        onNext={handleNext}
        // add back required
        required={!emailTemplateId || !html}
        spaceBetween
        // discardChanges={handleDiscardChanges}
      />
      <p className="font-semibold">1. Choose sender</p>
      <Dropdown
        id="dropdown1"
        elements={SENDERS.map((s) => ({ value: s, name: s }))}
        selectedElement={{ value: emailSender, name: emailSender }}
        didSelect={(e) => {
          dispatch(addEmailSender(e.value));
        }}
        className="px-3 py-2 max-w-lg"
        componentProps={{ className: "py-1" }}
      />
      <div className="flex flex-col bg-transparent rounded-lg">
        <p className="font-semibold text-left text-xl my-3 ">
            2. Select email template
        </p>
        <SearchBar
          id="EMAIL_TEMPLATE_SEARCH_BAR_ID"
          placeholder="Search By Template ID or Template Name"
          value={templateSearchInput}
          onChange={({ value }) => {
            searchTemplate(value);
          }}
          onMagnifyIconClick={searchTemplate}
        />
        <div className="flex flex-col h-full mt-2">
          <div className="rounded-sm border">
            {templates.slice(currentPage, pages).map((template, i) => (
              <div
                key={`t-${i}`}
                className={classnames("bg-white shadow-container border-b")}
              >
                <button
                  type="button"
                  className={classnames(
                    "flex w-full px-4 py-4 outline-0-important rounded-none",
                    {
                      "bg-green-300": emailTemplateId === template.id,
                    },
                  )}
                  onClick={async () => {
                    if (emailTemplateId === template.id) {
                      setHtml(null);
                      dispatch(addEmailTemplateId(""));
                      dispatch(addEmailTemplateVersionId(""));
                    } else {
                      const newVersionId = template.versions.find(
                        (t) => t.active,
                      )?.id;
                      setHtml(null);
                      dispatch(addEmailTemplateId(template.id));
                      dispatch(addEmailTemplateVersionId(newVersionId));
                      await fetchTemplate({
                        variables: {
                          templateId: template.id,
                          versionId: newVersionId,
                        },
                      });
                    }
                  }}
                >
                  <p>{template.name}</p>
                </button>
                {emailTemplateId === template.id ? (
                  <div className="px-8 pt-4 pb-4">
                    <div className="flex">
                      <p className="pr-1">Version: </p>
                      {template.versions.find((v) => v.id === emailTemplateVersionId).name}
                    </div>
                    <div className="pt-2 flex">
                      <p className="pr-2">Variables used:</p>
                      {html ? (
                        <p className="font-bold">
                          {emailTemplateVariables.length
                            ? emailTemplateVariables.join(", ")
                            : "None"}
                        </p>
                      ) : (
                        <p>Loading...</p>
                      )}
                    </div>
                    {html ? (
                      <div className="pt-2">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: html,
                          }}
                        />
                      </div>
                    ) : (
                      <p>Loading Preview...</p>
                    )}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </MarketingContainer>
  );
};

export default BatchEmail;
